/**
 * 站内信通知 Store
 * @author ritaxxu
 */
import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { IInnerMsgUnreadCount } from '@types'
import * as notificationService from '@services/notification'

interface NotificationState {
  /** 未读数统计 */
  unreadCount: IInnerMsgUnreadCount
  /** 消息面板是否展开 */
  panelVisible: boolean
  /** 重要通知横幅是否显示 */
  bannerVisible: boolean
  /** 轮询定时器 */
  pollTimer: ReturnType<typeof setInterval> | null
  /** 已「稍后再说」消忽略的重要未读消息表：recipientId → 静默截止时间戳（ms）；localStorage 持久化 */
  dismissedImportantMap: Record<number, number>

  /** 获取未读数 */
  fetchUnreadCount: () => Promise<void>
  /** 开始轮询（30s） */
  startPolling: () => void
  /** 停止轮询 */
  stopPolling: () => void
  /** 切换面板显示 */
  togglePanel: () => void
  /** 关闭面板 */
  closePanel: () => void
  /** 显示横幅 */
  showBanner: () => void
  /** 隐藏横幅（点「稍后再说」/「立即查看」触发） */
  hideBanner: () => void
}

// 「稍后再说」静默时长：2 小时
const DISMISS_DURATION_MS = 2 * 60 * 60 * 1000

// 重要未读 id 全局唯一，无需按账号隔离，固定 key 即可（规避 openid 异步就绪的竞态）
const DISMISSED_KEY = 'tcc_dismissed_important_ids'

/** 从 localStorage 读入历史快照（跨 tab 共享；关浏览器不清） */
const loadDismissed = (): Record<number, number> => {
  try {
    const raw = localStorage.getItem(DISMISSED_KEY)
    if (!raw) return {}
    const obj = JSON.parse(raw)
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return {}
    const result: Record<number, number> = {}
    Object.entries(obj).forEach(([k, v]) => {
      const id = Number(k)
      if (Number.isFinite(id) && typeof v === 'number') {
        result[id] = v
      }
    })
    return result
  } catch {
    return {}
  }
}

/** 写回 localStorage */
const persistDismissed = (map: Record<number, number>) => {
  try {
    localStorage.setItem(DISMISSED_KEY, JSON.stringify(map))
  } catch {
    // 存储失败静默，不影响主流程
  }
}

/**
 * 根据最新未读 ids 与已消忽略表，计算下一次的表状态与是否需要弹横幅
 * - ids 为空：清空表，不弹
 * - 存在 id 不在表中，或对应静默截止时间已过：视为需要提醒，弹
 * - 顺手剔除「已不在未读列表」的 id（已读/被删），防表膨胀
 */
const reconcileDismissed = (
  ids: number[],
  prev: Record<number, number>,
): { next: Record<number, number>, hasNew: boolean } => {
  if (ids.length === 0) {
    return { next: {}, hasNew: false }
  }
  const now = Date.now()
  const stillUnread = new Set(ids)
  const next: Record<number, number> = {}
  // 仅保留仍在未读列表里的 id 的静默记录
  Object.entries(prev).forEach(([k, v]) => {
    const id = Number(k)
    if (stillUnread.has(id)) {
      next[id] = v
    }
  })
  const hasNew = ids.some((id) => {
    const until = next[id]
    return until === undefined || until <= now
  })
  return { next, hasNew }
}

const initialState = {
  unreadCount: {
    unreadCount: 0,
    importantUnreadCount: 0,
    importantUnreadIds: [],
  } as IInnerMsgUnreadCount,
  panelVisible: false,
  bannerVisible: false,
  pollTimer: null as ReturnType<typeof setInterval> | null,
  dismissedImportantMap: loadDismissed(),
}

export const notificationStore = create<NotificationState>()(
  immer((set, get) => ({
    ...initialState,

    fetchUnreadCount: async () => {
      const [data] = await notificationService.getInnerMsgUnreadCount()
      if (!data) return

      const ids = data.importantUnreadIds || []
      // 每次都从 localStorage 重新读，保证跨 tab 同步（其他 tab 点稍后再说的写入本 tab 能感知）
      const { next, hasNew } = reconcileDismissed(ids, loadDismissed())

      set((state) => {
        state.unreadCount = { ...data, importantUnreadIds: ids }
        state.dismissedImportantMap = next
        if (hasNew) {
          state.bannerVisible = true
        } else if (state.bannerVisible) {
          // 其他 tab 已点稍后再说 → 本 tab 同步隐藏横幅
          state.bannerVisible = false
        }
      })
      persistDismissed(next)
    },

    startPolling: () => {
      const { fetchUnreadCount, pollTimer } = get()
      if (pollTimer) return // 已在轮询
      fetchUnreadCount() // 首次立刻调一次
      const timer = setInterval(() => {
        // visibilityState=hidden 时跳过
        if (document.visibilityState !== 'hidden') {
          fetchUnreadCount()
        }
      }, 30000)
      set((state) => { state.pollTimer = timer })
    },

    stopPolling: () => {
      const { pollTimer } = get()
      if (pollTimer) {
        clearInterval(pollTimer)
        set((state) => { state.pollTimer = null })
      }
    },

    togglePanel: () => {
      set((state) => { state.panelVisible = !state.panelVisible })
    },

    closePanel: () => {
      set((state) => { state.panelVisible = false })
    },

    showBanner: () => {
      set((state) => { state.bannerVisible = true })
    },

    hideBanner: () => {
      set((state) => {
        state.bannerVisible = false
        // 当前这批重要未读 id 全部静默 DISMISS_DURATION_MS，覆盖旧的截止时间
        // 基于 localStorage 最新值 merge，避免多 tab 场景下用陈旧内存值覆写他 tab 的写入
        const currentIds = state.unreadCount.importantUnreadIds || []
        const until = Date.now() + DISMISS_DURATION_MS
        const merged = { ...loadDismissed() }
        currentIds.forEach((id) => {
          merged[id] = until
        })
        state.dismissedImportantMap = merged
        persistDismissed(merged)
      })
    },
  })),
)
